import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Users, 
  FileText, 
  TrendingUp,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import apiService from '@/services/api';

export default function GradeCompletionPage() {
  const [year, setYear] = useState(12);
  const [term, setTerm] = useState('Term 1');
  const [loading, setLoading] = useState(true);
  const [completionData, setCompletionData] = useState(null);
  const [expandedClasses, setExpandedClasses] = useState({});

  useEffect(() => {
    fetchCompletionData();
  }, [year, term]);

  const fetchCompletionData = async () => {
    try {
      setLoading(true);
      const response = await apiService.request(`/api/admin/grade-completion/status/${year}/${encodeURIComponent(term)}`);
      setCompletionData(response);
    } catch (error) {
      console.error('Error fetching completion data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleClassExpansion = (classId) => {
    setExpandedClasses(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  const getStatusColor = (percentage) => {
    if (percentage >= 95) return 'text-green-600 bg-green-50';
    if (percentage >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getStatusIcon = (isComplete) => {
    return isComplete ? (
      <CheckCircle2 className="h-5 w-5 text-green-600" />
    ) : (
      <XCircle className="h-5 w-5 text-red-600" />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading grade completion data...</p>
        </div>
      </div>
    );
  }

  if (!completionData) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Failed to load grade completion data</AlertDescription>
      </Alert>
    );
  }

  const { overall_stats, classes } = completionData;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Grade Completion Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Check grade completion before publishing report cards
          </p>
        </div>
        
        {/* Year and Term Selectors */}
        <div className="flex gap-3">
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="11">Year 11</SelectItem>
              <SelectItem value="12">Year 12</SelectItem>
              <SelectItem value="13">Year 13</SelectItem>
            </SelectContent>
          </Select>

          <Select value={term} onValueChange={setTerm}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Term" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Term 1">Term 1</SelectItem>
              <SelectItem value="Term 2">Term 2</SelectItem>
              <SelectItem value="Term 3">Term 3</SelectItem>
              <SelectItem value="Term 4">Term 4</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Overall Status Alert */}
      {!overall_stats.ready_to_publish && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="font-semibold">
            ⚠️ NOT READY TO PUBLISH - {overall_stats.incomplete_classes} classes have incomplete grades
          </AlertDescription>
        </Alert>
      )}

      {overall_stats.ready_to_publish && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="font-semibold text-green-800">
            ✅ READY TO PUBLISH - All classes have complete grades
          </AlertDescription>
        </Alert>
      )}

      {/* Overall Statistics Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Total Classes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{overall_stats.total_classes}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Complete Classes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{overall_stats.complete_classes}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <XCircle className="h-4 w-4" />
              Incomplete Classes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{overall_stats.incomplete_classes}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Overall Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{overall_stats.overall_completion_percentage}%</div>
            <Progress value={overall_stats.overall_completion_percentage} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Grades Summary */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Students</p>
              <p className="text-2xl font-bold">{overall_stats.total_students}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Grades Needed</p>
              <p className="text-2xl font-bold">{overall_stats.total_grades_needed}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Grades Entered</p>
              <p className="text-2xl font-bold text-primary">{overall_stats.total_grades_entered}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Classes Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Classes Breakdown</CardTitle>
          <CardDescription>Click on a class to see details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {classes.map((classData) => (
              <div key={classData.class_id} className="border rounded-lg">
                <div 
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleClassExpansion(classData.class_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      {expandedClasses[classData.class_id] ? (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      )}
                      
                      {getStatusIcon(classData.is_complete)}
                      
                      <div className="flex-1">
                        <h3 className="font-semibold">{classData.class_name}</h3>
                        <p className="text-sm text-muted-foreground">Teacher: {classData.teacher_name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Students</p>
                        <p className="font-semibold">{classData.student_count}</p>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Assessments</p>
                        <p className="font-semibold">{classData.assessment_count}</p>
                      </div>
                      
                      <div className="text-right min-w-[100px]">
                        <Badge className={getStatusColor(classData.completion_percentage)}>
                          {classData.completion_percentage}%
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <Progress value={classData.completion_percentage} className="h-2" />
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedClasses[classData.class_id] && (
                  <div className="px-4 pb-4 pt-2 border-t bg-muted/20">
                    <div className="grid md:grid-cols-3 gap-4 mt-3">
                      <div>
                        <p className="text-sm font-medium">Expected Grades</p>
                        <p className="text-2xl font-bold">{classData.expected_grades}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-green-600">Entered</p>
                        <p className="text-2xl font-bold text-green-600">{classData.grades_entered}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-red-600">Missing</p>
                        <p className="text-2xl font-bold text-red-600">{classData.grades_missing}</p>
                      </div>
                    </div>

                    {classData.missing_students_sample.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium mb-2">Sample of students with missing grades:</p>
                        <div className="flex flex-wrap gap-2">
                          {classData.missing_students_sample.map((student, idx) => (
                            <Badge key={idx} variant="outline">{student}</Badge>
                          ))}
                        </div>
                        {classData.grades_missing > 5 && (
                          <p className="text-xs text-muted-foreground mt-2">
                            ...and {classData.grades_missing - 5} more missing grades
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Button */}
      <div className="flex justify-end">
        <Button 
          size="lg"
          disabled={!overall_stats.ready_to_publish}
          className="gap-2"
        >
          {overall_stats.ready_to_publish ? (
            <>
              <CheckCircle2 className="h-5 w-5" />
              Ready to Publish Reports
            </>
          ) : (
            <>
              <AlertTriangle className="h-5 w-5" />
              Complete Grades Before Publishing
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
